/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { useCallback, useState } from '@web-stories-wp/react';
import { useConfig } from '../../config';
import getStoryPropsToSave from '../utils/getStoryPropsToSave';
import { trackAppInsightsEvent } from '../../../../../../initAppInsights';

/**
 * Internal dependencies
 */
import { useAPI } from '../../api';
import { validateLottieTextRowsElement } from '../../../elements/lottie/textRows';

const getSavePhasePayload = (startedAt, payload = {}) => ({
  ...payload,
  durationMs: Date.now() - startedAt,
});

const getSavePhaseErrorPayload = (startedAt, error, payload = {}) => ({
  ...getSavePhasePayload(startedAt, payload),
  errorName: error?.name || 'Error',
  ...(error?.response?.status ? { responseStatus: error.response.status } : {}),
});

function useGeneratePages({
  storyId,
  story,
  pages,
  saveStory,
  deletedPagesIds,
}) {
  const {
    actions: { savePages },
  } = useAPI();
  const [isGeneratingPages, setIsGeneratingPages] = useState(false);
  const { metadata, getSaveContent } = useConfig();

  const generatePages = useCallback(
    async ({ addNewPagesAtTheEnd }) => {
      setIsGeneratingPages(true);
      return Promise.resolve()
        .then(async () => {
          for (const [pageIndex, page] of pages.entries()) {
            const issues = await Promise.all(
              (page.elements || []).map(validateLottieTextRowsElement)
            );
            const issue = issues.find(Boolean);
            if (issue) {
              const error = new Error(issue);
              error.name = 'LottieTextRowsError';
              error.pageNumber = pageIndex + 1;
              throw error;
            }
          }
          const preparationStartedAt = Date.now();
          const preparationPayload = { pageCount: pages?.length || 0 };
          trackAppInsightsEvent(
            'Studio: Save Preparation Started',
            preparationPayload
          );
          let preparedProperties;
          try {
            preparedProperties = await getStoryPropsToSave({
              story,
              pages,
              metadata,
              getSaveContent,
            });
            trackAppInsightsEvent(
              'Studio: Save Preparation Completed',
              getSavePhasePayload(preparationStartedAt, {
                ...preparationPayload,
                contentBytes: new Blob([preparedProperties.content || '']).size,
              })
            );
          } catch (error) {
            trackAppInsightsEvent(
              'Studio: Save Preparation Failed',
              getSavePhaseErrorPayload(
                preparationStartedAt,
                error,
                preparationPayload
              )
            );
            throw error;
          }
          const storyToSave = { storyId, ...preparedProperties };
          const pageCount = storyToSave.pages?.length || 0;

          return saveStory(undefined, preparedProperties).then(() => {
            const savePagesStartedAt = Date.now();
            trackAppInsightsEvent('Studio: Save Pages Started', {
              pageCount,
            });

            return savePages({
              storyToSave,
              story,
              deletedPagesIds,
              addNewPagesAtTheEnd,
            })
              .then((result) => {
                trackAppInsightsEvent(
                  'Studio: Save Pages Completed',
                  getSavePhasePayload(savePagesStartedAt, {
                    pageCount,
                  })
                );

                return result;
              })
              .catch((e) => {
                trackAppInsightsEvent(
                  'Studio: Save Pages Failed',
                  getSavePhaseErrorPayload(savePagesStartedAt, e, {
                    pageCount,
                  })
                );
                throw e;
              });
          });
        })
        .finally(() => setIsGeneratingPages(false));
    },
    [
      pages,
      storyId,
      story,
      metadata,
      getSaveContent,
      saveStory,
      savePages,
      deletedPagesIds,
    ]
  );

  return { generatePages, isGeneratingPages };
}

export default useGeneratePages;
